import type { DsTableSort } from '@gde-mit/angular-design-system';

import type { ManagedTeacherUser } from '../models/managed-teacher-user';
import { managedTeacherFullName } from './managed-teacher-user.util';

/**
 * Returns a copy of the user list sorted by the table's primary sort column.
 *
 * @param users - Rows to sort.
 * @param sort - Design-system table sort state (first entry wins).
 * @param locale - Active Transloco language for name and email collation.
 * @returns New sorted array.
 */
export function sortManagedTeachers(
  users: readonly ManagedTeacherUser[],
  sort: readonly DsTableSort[],
  locale: string,
): ManagedTeacherUser[] {
  if (sort.length === 0) {
    return [...users];
  }

  const { key, direction } = sort[0];
  const sortDirection = direction === 'desc' ? 'desc' : 'asc';

  return [...users].toSorted((left, right) => compareManagedTeachersByKey(left, right, key, sortDirection, locale));
}

/**
 * Compares two rows for one table sort column.
 *
 * @param left
 * @param right
 * @param key
 * @param direction
 * @param locale
 */
function compareManagedTeachersByKey(
  left: ManagedTeacherUser,
  right: ManagedTeacherUser,
  key: string,
  direction: 'asc' | 'desc',
  locale: string,
): number {
  const multiplier = direction === 'desc' ? -1 : 1;

  switch (key) {
    case 'invitedAt': {
      return left.invitedAt.localeCompare(right.invitedAt) * multiplier;
    }
    case 'lastLoginAt': {
      const leftHasLogin = left.lastLoginAt !== null;
      const rightHasLogin = right.lastLoginAt !== null;
      if (!leftHasLogin && !rightHasLogin) {
        return 0;
      }
      if (!leftHasLogin) {
        return 1;
      }
      if (!rightHasLogin) {
        return -1;
      }
      return (left.lastLoginAt ?? '').localeCompare(right.lastLoginAt ?? '') * multiplier;
    }
    case 'fullName': {
      return (
        managedTeacherFullName(left, locale).localeCompare(managedTeacherFullName(right, locale), locale) * multiplier
      );
    }
    case 'email': {
      return left.email.localeCompare(right.email, locale) * multiplier;
    }
    case 'status': {
      return left.status.localeCompare(right.status) * multiplier;
    }
    default: {
      return 0;
    }
  }
}
